import {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode,
} from "react";

const TENANT_KEY = "cg_tenant";

interface TenantContextValue {
  tenantId: string | null;
  setTenantId: (id: string) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// Module-level accessor for the API client (avoids hook dependency in non-component code).
let activeTenantId: string | null = sessionStorage.getItem(TENANT_KEY);

/**
 * Module-level getter for the current tenant ID.
 * Used by the API client which cannot use React hooks.
 *
 * Returns:
 *   The active tenant ID, or null if not set.
 */
export function getTenantId(): string | null {
  return activeTenantId;
}

/**
 * Provides tenant context to child components.
 * Synchronizes tenant ID with the module-level accessor and sessionStorage.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenantId, setTenantIdState] = useState<string | null>(
    () => sessionStorage.getItem(TENANT_KEY),
  );

  const setTenantId = (id: string) => {
    activeTenantId = id;
    sessionStorage.setItem(TENANT_KEY, id);
    setTenantIdState(id);
  };

  const value = useMemo(() => ({ tenantId, setTenantId }), [tenantId]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/**
 * Returns the current tenant context.
 *
 * Raises:
 *   Error: If used outside a TenantProvider.
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (context === null) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}
